import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import React from 'react';

import { useClockSkewDetection } from '@hooks/useClockSkewDetection';
import { isElectron } from '@/renderer/util';

/**
 * Shown when the device system clock is more than 5 minutes out of sync.
 * Supabase uses JWT timestamps for auth validation — a skewed clock causes
 * silent sign-outs with no visible reason. This component surfaces the cause
 * and guides the user to fix their system time.
 *
 * Mount once near the root of the app (App.tsx). Renders nothing when the
 * clock is fine.
 */
export const SystemClockWarning: React.FC = () => {
  const isSkewed = useClockSkewDetection();

  if (!isElectron() || !isSkewed) return null;

  const isWindows =
    typeof navigator !== 'undefined' && navigator.userAgent.includes('Windows');

  const fixInstructions = isWindows
    ? 'Open Settings → Time & Language → Date & Time → turn on "Set time automatically".'
    : 'Open System Settings → General → Date & Time → enable "Set time and date automatically".';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-xl border border-amber-200 bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Incorrect device date or time detected
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              smartPOS Cloud verifies your identity using your device&apos;s
              system clock. When the date or time is significantly off, your
              session is rejected and you are signed out automatically — with no
              visible reason.
            </p>
          </div>
        </div>

        <div className="mb-5 rounded-lg border border-amber-100 bg-amber-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              How to fix
            </span>
          </div>
          <p className="text-sm text-amber-700">{fixInstructions}</p>
        </div>

        <p className="mb-5 text-xs text-gray-500">
          Once your system clock is correct, click the button below to reload
          the app. Your data is safe and will sync automatically when you log
          back in.
        </p>

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-pos-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-pos-primary/40"
        >
          <RefreshCw className="h-4 w-4" />
          I&apos;ve fixed it — Reload the app
        </button>
      </div>
    </div>
  );
};
